import { useEffect, useRef } from "react";

import { Column } from "@/components/Column";
import { Text } from "@/components/Text";
import { SadieSimulatorComposer } from "@/components/tools/sadie-simulator/SadieSimulatorComposer";
import { SadieSimulatorMessageBubble } from "@/components/tools/sadie-simulator/SadieSimulatorMessageBubble";
import { SadieSimulatorTypingDots } from "@/components/tools/sadie-simulator/SadieSimulatorTypingDots";

import { SadieSimulatorTapback, SadieSimulatorThread as Thread } from "@/types";

export const SadieSimulatorThread = ({
  isSending,
  onSend,
  onTapback,
  thread,
}: {
  isSending: boolean;
  onSend: (body: string) => Promise<void>;
  onTapback: (quoId: string, reaction: SadieSimulatorTapback) => void;
  thread: Thread | undefined;
}) => {
  /* React Hooks */
  const bottomRef = useRef<HTMLDivElement>(null);
  const messages = thread ? thread.messages : [];
  const isDotsUp = thread ? thread.dotsUp : false;
  const isSimulating = !!thread?.simulator;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages.length, isDotsUp]);

  /* Render */
  return (
    <Column className="h-full min-h-0 bg-white">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Column className="mx-auto min-h-full w-full max-w-2xl gap-4 px-6 py-8">
          {messages.length === 0 && !isDotsUp ? (
            <EmptyThread isSimulating={isSimulating} />
          ) : null}
          {messages.map((message) => (
            <SadieSimulatorMessageBubble
              canTapBack={isSimulating}
              key={message.id}
              message={message}
              onTapback={onTapback}
            />
          ))}
          {isDotsUp ? <SadieSimulatorTypingDots /> : null}
          <div ref={bottomRef} />
        </Column>
      </div>
      <SadieSimulatorComposer
        isDisabled={!isSimulating}
        isSending={isSending}
        onSend={onSend}
      />
    </Column>
  );
};

const EmptyThread = ({ isSimulating }: { isSimulating: boolean }) => (
  <Text className="m-auto text-center text-sm font-normal text-slate-500">
    {isSimulating
      ? "Sadie's opener shows up here, then you text as the tester."
      : "Start over to begin. Sadie sends her opener, then you text as the tester."}
  </Text>
);
